import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  IconButton,
  MenuItem,
  Dialog,
  DialogContent,
  DialogActions,
  Backdrop,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { ColorSchemes } from './ChartOptions/Color/ColorScheme';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FetchData from './ServerCall/FetchData';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import { Tooltip } from '@mui/material';
import { ThemeObject } from './ThemeEditor';
import { AcceptRejectDialog } from './CommonFunctions/DialogComponents';
import { isColorDark } from './ChartOptions/Color/ChartColorComponents/colorUtils';
import { useDispatch, useSelector } from 'react-redux';
import { setDefaultThemeId } from '../redux/TabTile/TabActions';
import { RootState } from '../redux';
import fetchUserThemes from '../api/fetchUserThemes';
import { clearCachedThemes } from '../api/fetchUserThemes';
import Menu from '@mui/material/Menu';
import DownloadIcon from '@mui/icons-material/Download';

export interface UserTheme {
  themeId: string;
  themeName: string;
  background: string;
  tableHeaderBackground: string;
  colors: string[];
  isDefault?: boolean;
}

interface SavedThemesProps {
  onCreateNew: () => void;
  onDeleteTheme: (themeId: string) => void;
  onEditTheme: (theme: ThemeObject) => void;
  onUserThemesUpdate?: (themes: UserTheme[]) => void;
  refreshThemes: () => Promise<void>;
}

const darkThemeNames = ['chalk', 'dark', 'halloween', 'purplePassion'];

const SavedThemes: React.FC<SavedThemesProps> = ({
  onCreateNew,
  onDeleteTheme,
  onEditTheme,
  onUserThemesUpdate,
  refreshThemes,
}) => {
  const [viewMode, setViewMode] = useState<'silzila' | 'user'>(
    () => (localStorage.getItem('viewMode') as 'silzila' | 'user') || 'silzila',
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [userThemes, setUserThemes] = useState<UserTheme[]>([]);

  const maxRows = viewMode === 'silzila' ? ColorSchemes.length : userThemes.length;

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [themeToDelete, setThemeToDelete] = useState<UserTheme | null>(null);

  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');


  const dispatch = useDispatch();
  const tabId = useSelector((state: RootState) => state.tabTileProps.selectedTabId);
  const defaultThemeId = useSelector(
    (state: RootState) => state.tabState.tabs[tabId]?.dashboardState.defaultThemeId,
  );

  const loadUserThemes = async (forceRefresh = false) => {
    if (forceRefresh) {
      clearCachedThemes();
    }

    const { themes, defaultThemeId } = await fetchUserThemes();

    setUserThemes(themes);

    if(tabId)dispatch(setDefaultThemeId(tabId, defaultThemeId));

    if (onUserThemesUpdate) onUserThemesUpdate(themes);
  };


useEffect(() => {
  loadUserThemes();
}, [tabId]);


  const handleDeleteConfirmed = async (themeId: string) => {
    try {
      setLoading(true);
      await FetchData({
        requestType: 'noData',
        method: 'DELETE',
        url: `theme/${themeId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      clearCachedThemes();
      await loadUserThemes();
      setUserThemes((prevThemes) => prevThemes.filter((theme) => theme.themeId !== themeId));
      onDeleteTheme(themeId);
      setShowDeleteDialog(false);
      setThemeToDelete(null);
    } catch (error) {
      console.error('Failed to delete theme:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleDownloadTheme = async (themeId: string, themeName: string) => {
    try {
      setLoading(true);
      const response = await FetchData({
        requestType: 'noData',
        method: 'GET',
        url: `theme/${themeId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      const themeData = response.data;
      const rawTheme = JSON.parse(themeData.theme);
      const chartColorsArray: string[] = (rawTheme.colors || []).map((color: string) => {
        if (typeof color === 'string' && color.includes(':')) {
          return color.split(':')[1].trim();
        }
        return color.trim();
      });

      const customFormattedTheme = {
        themes: {
          themeName: themeData.themeName,
          colorTheme: {
            tableBackground: rawTheme.background,
            tableHeaderBackground: rawTheme.tableHeaderBackground,
            chartColors: chartColorsArray.map((color, index) => `color${index + 1}: ${color}`),
          },
        },
      };
      const blob = new Blob([JSON.stringify(customFormattedTheme, null, 2)], {
        type: 'application/json',
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${themeData.themeName}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download theme:', error);
    } finally {
      setLoading(false);
    }
  };

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [importCandidate, setImportCandidate] = useState<any>(null);
  const [conflictTheme, setConflictTheme] = useState<UserTheme | null>(null);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);

  const handleImportClick = () => {
    fileInputRef.current?.click();
    handleClose(); // Close menu after clicking
  };

  const validateThemeStructure = (theme: any): boolean => {
    let isValid = true;

    const isEmpty = (val: any) => !val || !val.toString().trim();

    if (!theme || typeof theme !== 'object') {
      isValid = false;
    }

    const t = theme;
    if (!('themeName' in t) || isEmpty(t.themeName)) {
      isValid = false;
    }
    if (!t?.colorTheme || typeof t.colorTheme !== 'object') {
      isValid = false;
    } else {
      if (Object.keys(t.colorTheme).length === 0) {
        isValid = false;
      } else {
        if (isEmpty(t.colorTheme.tableBackground) || isEmpty(t.colorTheme.tableHeaderBackground)) {
          isValid = false;
        }

        const colors = t.colorTheme.chartColors;
        if (!colors) {
          isValid = false;
        } else if (Array.isArray(colors)) {
          if (colors.length < 5) {
            isValid = false;
          }
        } else if (typeof colors === 'object') {
          const requiredKeys = Array.from({ length: 10 }, (_, i) => `color${i + 1}`);
          requiredKeys.forEach((key) => {
            if (!(key in colors) || isEmpty(colors[key])) {
              isValid = false;
            }
          });
        } else {
          isValid = false;
        }
      }
    }

    if (!isValid) {
      setDialogMessage('Theme upload failed — The file is not in proper shape.');
      setDialogOpen(true);
      return false;
    }

    return true;
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const importedJSON = JSON.parse(text);
      const importedTheme = importedJSON?.themes ?? importedJSON;

      // Validate before continuing
      if (!validateThemeStructure(importedTheme)) {
        return;
      }

      // Remove themeId so backend assigns new one
      if ('themeId' in importedTheme) {
        delete importedTheme.themeId;
      }

      const importedName = importedTheme?.themeName?.toString?.() ?? importedTheme?.name ?? null;
      const chartColorsObj =
        importedTheme?.colorTheme?.chartColors ?? importedTheme?.chartColors ?? {};

      let colors: string[] = [];
      if (Array.isArray(importedTheme.colorTheme.chartColors)) {
        colors = importedTheme.colorTheme.chartColors.map((item: string) => item.trim());
        importedTheme.colorTheme.chartColors = colors;
      } else if (typeof chartColorsObj === 'object' && chartColorsObj !== null) {
        colors = Object.values(chartColorsObj as Record<string, string>).map((c) => c.trim());
        importedTheme.colorTheme.chartColors = colors;
      }

      // Final sanity check
      if (!colors || colors.length < 5 || !importedName) {
        setDialogMessage('Theme upload failed — The file is not in proper shape.');
        setDialogOpen(true);
        return;
      }

      // Check for existing theme with same name
      const existing = userThemes.find(
        (t) => t.themeName?.toLowerCase() === importedName.toLowerCase(),
      );

      setImportCandidate(importedTheme);

      if (existing) {
        setDialogMessage(
          'Theme name already exists. If you want to make changes, use the edit mode.',
        );
        setDialogOpen(true);
      } else {
        await applyImport(importedTheme, null);
      }
    } catch (err) {
      setDialogMessage('Theme upload failed — The file is not in proper shape.');
      setDialogOpen(true);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const applyImport = async (importedThemeRaw: any, existingTheme: UserTheme | null) => {
    try {
      setLoading(true);
      const chartColorsObj =
        importedThemeRaw?.colorTheme?.chartColors ?? importedThemeRaw?.chartColors ?? {};

      let colors = Array.isArray(chartColorsObj) ? chartColorsObj : Object.values(chartColorsObj);
      colors = colors.map((c: string) => (c.includes(':') ? c.split(':')[1].trim() : c.trim()));

      const themeObj = {
        background:
          importedThemeRaw?.colorTheme?.tableBackground ??
          importedThemeRaw?.background ??
          '#ffffff',
        tableHeaderBackground:
          importedThemeRaw?.colorTheme?.tableHeaderBackground ??
          importedThemeRaw?.tableHeaderBackground ??
          '#ffffff',
        colors,
      };

      // Prevent overwrite if theme already exists
      if (existingTheme) {
        setDialogMessage(`Theme "${importedThemeRaw.themeName}" already exists. Import cancelled.`);
        setDialogOpen(true);
        return;
      }

      // CREATE NEW THEME
      const response = await FetchData({
        requestType: 'withData',
        method: 'POST',
        url: `theme`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        data: {
          themeName: importedThemeRaw.themeName,
          theme: JSON.stringify(themeObj),
        },
      });

      console.log('Theme created successfully:', response);

      clearCachedThemes();
      await loadUserThemes();

      // Refresh themes in parent component
      if (refreshThemes) {
        await refreshThemes();
      }
      setDialogMessage('Theme imported successfully.');
      setDialogOpen(true);
    } catch (error) {
      console.error('Failed to save theme:', error);
      setDialogMessage('Failed to import theme. Please try again.');
      setDialogOpen(true);
    } finally {
      setLoading(false);
      setImportCandidate(null);
      setConflictTheme(null);
      setShowOverrideDialog(false);
    }
  };
  return (
    <Box>
      <input
        type="file"
        accept=".json"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <Typography variant="h6" fontWeight="bold" mb={3} fontSize="16px" color="rgb(72,72,72)">
        Theme
      </Typography>

      <Box
        display="flex"
        flexWrap={{ xs: 'wrap', md: 'nowrap' }}
        justifyContent="center"
        alignItems="center"
        gap={{ xs: 18, md: 40 }}
        mb={2}
        minHeight="40px"
      >
        <Box
          sx={{
            display: 'flex',
            borderRadius: '6px',
            overflow: 'hidden',
            width: 200,
            marginLeft: '60px',
          }}
        >
          <Box
            onClick={() => {
              setViewMode('silzila');
              localStorage.setItem('viewMode', 'silzila');
            }}
            sx={{
              width: '100px',
              textAlign: 'center',
              padding: '3px 6px',
              cursor: 'pointer',
              backgroundColor: viewMode === 'silzila' ? '#2bb9bb' : '#f9f9f9',
              color: viewMode === 'silzila' ? 'white' : '#333',
              fontSize: '12px',
              transition: 'background-color 0.3s ease',
              boxSizing: 'border-box',
            }}
          >
            Silzila Theme
          </Box>

          <Box
            onClick={() => {
              setViewMode('user');
              localStorage.setItem('viewMode', 'user');
            }}
            sx={{
              width: '100px',
              textAlign: 'center',
              padding: '3px 6px',
              cursor: 'pointer',
              backgroundColor: viewMode === 'user' ? '#2bb9bb' : '#f9f9f9',
              color: viewMode === 'user' ? 'white' : '#333',
              fontSize: '12px',
              transition: 'background-color 0.3s ease',
              boxSizing: 'border-box',
            }}
          >
            User Theme
          </Box>
        </Box>

        <Box sx={{ height: '36px', display: 'flex', alignItems: 'center' }}>
          {viewMode === 'user' ? (
            <>
              <Button
                variant="contained"
                startIcon={
                  <AddIcon
                    style={{ height: '15px', width: '15px', transform: 'TranslateY(-1px)' }}
                  />
                }
                onClick={handleClick}
                sx={{
                  minWidth: 90,
                  backgroundColor: 'white',
                  color: '#2bb9bb',
                  boxShadow: 'none',
                  padding: '3px 6px',
                  transform: 'translateX(-70px)',
                  boxSizing: 'border-box',
                  transition: 'all 0.3s ease',
                  border: '1px solid #2bb9bb',
                  marginRight: '6px',
                  '&:hover': {
                    backgroundColor: '#2bb9bb',
                    color: 'white',
                    boxShadow: 'none',
                  },
                }}
              >
                New Theme
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <MenuItem
                  onClick={() => {
                    handleClose();
                    onCreateNew();
                  }}
                >
                  Create Theme
                </MenuItem>

                <MenuItem onClick={handleImportClick}>Import Theme</MenuItem>
              </Menu>
            </>
          ) : (
            <Box sx={{ width: 110 }} />
          )}
        </Box>
      </Box>

      <Box sx={{ maxWidth: 570, margin: '0 auto', overflowX: 'auto' }}>
        <Paper
          elevation={0}
          style={{
            maxHeight: '72vh',
            overflowY: 'auto',
            border: 'none',
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {viewMode === 'silzila' && (
                  <TableCell
                    sx={{
                      width: '100%',
                      textAlign: 'center',
                      fontSize: '14px',
                      color: 'rgb(72,72,72)',
                    }}
                    align="center"
                  >
                    <strong>Silzila Themes</strong>
                  </TableCell>
                )}
                {viewMode === 'user' && (
                  <>
                    <TableCell
                      sx={{
                        width: '75%',
                        textAlign: 'center',
                        fontSize: '14px',
                        color: 'rgb(72,72,72)',
                      }}
                      align="center"
                    >
                      <strong>User Themes</strong>
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '35%',
                        textAlign: 'center',
                        fontSize: '14px',
                        color: 'rgb(72,72,72)',
                      }}
                      align="center"
                    >
                      <strong>Actions</strong>
                    </TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {[...Array(maxRows)].map((_, index) => {
                const silzilaTheme = ColorSchemes[index];
                const userTheme = userThemes[index];

                const isSelected = userTheme?.themeId === defaultThemeId;

                return (
                  <TableRow
                    key={index}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                      '& td': {
                        borderTop: isSelected ? '1px solid black' : '1px solid #e0e0e0',
                        borderBottom: isSelected ? '1px solid black' : '1px solid #e0e0e0',
                        transition: 'border-color 0.2s ease',
                      },
                      '&:hover .action-icon': {
                        color: (theme) => theme.palette.primary.main,
                      },
                      '&:hover .delete-icon': {
                        color: (theme) => theme.palette.error.main,
                      },
                    }}
                  >
                    {viewMode === 'silzila' && (
                      <TableCell style={{ padding: '10px 3px' }}>
                        {silzilaTheme ? (
                          <div
                            style={{
                              backgroundColor: silzilaTheme.background,
                              borderRadius: '6px',
                              padding: '6px',
                              color: darkThemeNames.includes(silzilaTheme.name)
                                ? 'white'
                                : '#3b3b3b',
                              marginLeft: '25px',
                              marginRight: '25px',
                              border: '1px solid grey',
                            }}
                          >
                            <div
                              style={{
                                fontSize: '12px',
                                marginBottom: '4px',
                                textTransform: 'capitalize',
                              }}
                            >
                              {silzilaTheme.name}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                              {silzilaTheme.colors.map((color, i) => (
                                <div
                                  key={i}
                                  style={{
                                    height: '8px',
                                    width: '16px',
                                    borderRadius: '2px',
                                    background: color,
                                    border: '1px solid white',
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </TableCell>
                    )}

                    {viewMode === 'user' && userTheme && (
                      <>
                        <TableCell style={{ padding: '10px 3px' }}>
                          <div
                            style={{
                              position: 'relative',
                              marginLeft: '35px',
                              marginRight: '25px',
                            }}
                          >
                            {defaultThemeId === userTheme.themeId && (
                              <Tooltip
                                title="This is set as default"
                                arrow
                                slotProps={{
                                  tooltip: { sx: { fontSize: '12px' } },
                                }}
                              >
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    left: '-30px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <ColorLensIcon
                                    sx={{
                                      fontSize: '20px',
                                      color: 'grey.500',
                                      cursor: 'pointer',
                                    }}
                                  />
                                </Box>
                              </Tooltip>
                            )}

                            <div
                              style={{
                                backgroundColor: userTheme.background,
                                borderRadius: '6px',
                                padding: '6px',
                                border: '1px solid grey',
                                color: isColorDark(userTheme.background) ? 'white' : '#3b3b3b',
                              }}
                            >
                              <div
                                style={{
                                  fontSize: '12px',
                                  marginBottom: '4px',
                                  textTransform: 'capitalize',
                                  fontWeight: isSelected ? 'bold' : 'normal',
                                }}
                              >
                                {userTheme.themeName}
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                {userTheme.colors.map((color, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      height: '8px',
                                      width: '16px',
                                      borderRadius: '2px',
                                      background: color,
                                      border: '1px solid white',
                                    }}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                        </TableCell>

                        <TableCell align="center">
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Tooltip
                              title="Delete"
                              arrow
                              slotProps={{ tooltip: { sx: { fontSize: '12px' } } }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setThemeToDelete(userTheme);
                                  setShowDeleteDialog(true);
                                }}
                                sx={{ color: 'grey.300', mr: 1 }}
                              >
                                <DeleteIcon fontSize="small" className="delete-icon" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title="Edit"
                              arrow
                              slotProps={{ tooltip: { sx: { fontSize: '12px' } } }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => onEditTheme(userTheme)}
                                sx={{ color: 'grey.300', mr: 0.5 }}
                              >
                                <EditIcon fontSize="small" className="action-icon" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title="Download"
                              arrow
                              slotProps={{ tooltip: { sx: { fontSize: '12px' } } }}
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleDownloadTheme(userTheme.themeId, userTheme.themeName)
                                }
                                sx={{ color: 'grey.300' }}
                              >
                                <DownloadIcon fontSize="small" className="action-icon" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={isSelected ? 'Remove Default' : 'Click to set as Default'}
                              arrow
                              slotProps={{ tooltip: { sx: { fontSize: '12px' } } }}
                            >
                              <IconButton
                                size="small"
                                sx={{ marginLeft: '6px' }}
                                onClick={async () => {
                                  try {
                                    setLoading(true);
                                    if (isSelected) {
                                      await FetchData({
                                        requestType: 'noData',
                                        method: 'PUT',
                                        url: `theme/remove-default`,
                                        headers: {
                                          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                                        },
                                      });
                                      dispatch(setDefaultThemeId(tabId, null));
                                    } else {
                                      await FetchData({
                                        requestType: 'noData',
                                        method: 'PUT',
                                        url: `theme/set-default?themeId=${userTheme.themeId}`,
                                        headers: {
                                          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                                        },
                                      });
                                      dispatch(setDefaultThemeId(tabId, userTheme.themeId));
                                    }
                                  } catch (error) {
                                    console.error('Failed to update default theme:', error);
                                  } finally {
                                    setLoading(false);
                                  }
                                  setUserThemes((prevThemes) =>
                                    prevThemes.map((theme) => ({
                                      ...theme,
                                      isDefault: theme.themeId === userTheme.themeId,
                                    })),
                                  );
                                }}
                              >
                                {isSelected ? (
                                  <CheckCircleIcon fontSize="small" sx={{ color: 'grey' }} />
                                ) : (
                                  <RadioButtonUncheckedIcon
                                    fontSize="small"
                                    sx={{ color: 'grey' }}
                                  />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <AcceptRejectDialog
        open={showDeleteDialog}
        closeFunction={() => {
          setShowDeleteDialog(false);
          setThemeToDelete(null);
        }}
        rejectFunction={() => {
          setShowDeleteDialog(false);
          setThemeToDelete(null);
        }}
        acceptFunction={() => {
          if (themeToDelete) {
            handleDeleteConfirmed(themeToDelete.themeId);
          }
        }}
        heading="Delete Theme"
        messages={[
          {
            text: `Are you sure you want to delete the theme "${themeToDelete?.themeName ?? ''}"?`,
          },
        ]}
        acceptText="Delete"
        rejectText="Cancel"
        varient="animated"
        rejectButtonStyle={{
          border: '2px solid red',
          color: 'red',
        }}
        acceptButtonStyle={{
          border: '2px solid #2bb9bb',
          color: '#2bb9bb',
        }}
        acceptTransition={{
          transitionTime: '0.2s',
          backgroundColor: 'secondary.contrastText',
          color: '#2bb9bb',
          hoverBackgroundColor: '#2bb9bb',
          hoverColor: 'secondary.contrastText',
        }}
        rejectTransition={{
          transitionTime: '0.2s',
          backgroundColor: 'secondary.contrastText',
          color: 'red',
          hoverBackgroundColor: 'red',
          hoverColor: 'secondary.contrastText',
        }}
      />
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogContent>{dialogMessage}</DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            style={{ border: '1px solid #2bb9bb', padding: '1px' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 2 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default SavedThemes;
